import { Component, OnInit } from '@angular/core';
import { MessageBoxComponent } from '@common/popups/message-box/message-box.component';
import { LayoutService } from '@common/services/layout.service';
import { ModalService } from '@common/services/modal.service';
import { CalendarComponent } from '@common/UI/calendar/calendar.component';
import { NumberInputComponent } from '@common/UI/number-input/number-input.component';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';
import { Filterable } from '@common/utilities/Filterable';
import { Lookup } from '@common/utilities/Lookup';
import { commit, main } from '@core/operators';
import { ApiService } from '@core/services/api.service';
import { ConfigsService } from '@core/services/configs.service';
import { DataService, InvoiceAllocationRecord, InvoiceCategory, InvoiceDetailRecord, InvoiceRecord, InvoiceState, invoiceState, InvoiceType, OrderDetailRecord, OrderRecord, OrderState, PartnerRecord, parts2SKU, ProductRecord, ProductUnit } from '@core/services/data.service';
import { PartnersService } from '@core/services/partners.service';
import { ProductsService } from '@core/services/products.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { Multilingual } from '@core/utilities/multilingual';
import { TagEditorComponent } from '@routes/editors/tag-editor/tag-editor.component';
import { TextEditorComponent } from '@routes/editors/text-editor/text-editor.component';
import { ViewerComponent } from '@routes/invoices/viewer/viewer.component';
import { map } from 'rxjs/operators';
import { ConfirmerComponent } from './confirmer/confirmer.component';


interface Lookups {
  products: Lookup<OrderDetail>;
  orders: Lookup<OrderRecord>;
}

const lu: Lookups = {
  products: new Lookup<OrderDetail>(i => i.OrderID + "/" + i.SKU + "/" + i.Color + "/" + i.ImportUnitPrice),
  orders: new Lookup<OrderRecord>(i => i.ID)
};

interface Group {
  OrderID: number;
  OrderTag: string;
  Partners: Partner[];
  SKU: string;
  Color: string;
  Unit: ProductUnit;
  Reduced: number;
  QuotePrice: number;
  IsPriceEditable: boolean;
  records: InvoiceDetailRecord[];
}

@Component({
  templateUrl: './imports.component.html',
  styleUrls: ['./imports.component.scss']
})
export class ImportsComponent implements OnInit {

  public importData(text: string = '[ĐƠN ĐẶT] [NCC] [MÃ HÀNG] [SỐ LƯỢNG] [CÂY 1] [CÂY 2] [CÂY 3] [CÂY 4] [CÂY 5] [CÂY 6] [DÀI TÍNH TIỀN] [GIÁ] [THÀNH TIỀN]') {

    this.modal.show(TextEditorComponent, { state: { title: 'Nhập hàng từ xưởng bằng dữ liệu excel', text: text } }).content.commit.subscribe(text => {

      this.sync.when(ProductsService, ConfigsService).commit(() => {

        let records: { sku: string, color: string, order: number, price: number, actualLength: number, quoteLength: number }[] = [];

        {
          let lines = text.split('\n');

          let code: string = '';
          let sku: string = '';
          let color: string = '';
          let tag: string = '';

          let orders: { [key: string]: number } = {};
          lu.orders.source.forEach(i => orders[i.Tag] = i.ID);

          for (let line of lines) {

            let cols = line.split('\t');

            if (cols.length < 4) continue;

            cols[0] = cols[0].toUpperCase().replace(/([^A-Z0-9\\.]+)/i, '');

            if (cols[0] != '') tag = cols[0];

            cols[2] = cols[2].replace(/(#|\-)/, '');

            if (cols[1] != '') code = cols[1];

            if (cols[2] != '') {

              if (cols[2].indexOf(code) == 0) {
                sku = parts2SKU('', cols[2]);
              }
              else {
                sku = parts2SKU(code, cols[2]);
              }
            }

            if (cols[3] != '') color = cols[3].split(/(-|~|号|\()/i)[0].replace(/(#)/i, '').toUpperCase();

            if (sku != '' && color != '') {

              for (let i = 5; i < 11; i++) {
                let length = parseFloat(cols[i].replace(',',''));

                if (!isNaN(length)) records.push({
                  sku: sku,
                  order: tag != null && typeof orders[tag] != 'undefined' ? orders[tag] : 0,
                  color: color,
                  price: NaN,
                  actualLength: length,
                  quoteLength: NaN
                })
              }
            }

            if (cols[11] != '') {
              let length = parseFloat(cols[11].replace(',', ''));
              let price = parseFloat(cols[12].replace(',', ''));

              let batch = records.filter(i => isNaN(i.price));

              let discount = Math.round((batch.sum(i => i.actualLength) - length) * 10 / batch.length) / 10;

              batch.forEach(i => {
                i.price = price;
                i.quoteLength = discount > 0 ? i.actualLength - discount : i.actualLength;
              });
            }
          }
        }

        let adjustments: { [key: string]: ProductRecord } = {};
        let news: { sku: string, color: string, logistics: number, profit: number, price: number }[] = [];

        records.forEach(record => {

          let products = this.products.get(record.sku);

          let key = [record.sku, record.price].join('/');

          if (products.length < 1) {

            if (!adjustments[key]) {
              adjustments[key] = { ID: 0, SKU: record.sku, Colors: [], Unit: ProductUnit.Meter, AverageQuantity: 0, UnitPrice: record.price, IsPriceVerified: false, LogisticsMargin: this.configs.defaultLogisticsMargin, ProfitMargin: this.configs.defaultProfitMargin };
            }            
          }

          for (let o in adjustments) {            
            if (adjustments[o].SKU == record.sku) {

              if (adjustments[o].ID > 0) products = products.filter(i => i.ID != adjustments[o].ID);
              products.push(adjustments[o]);
            }
          }

          let colors = products.many(i => i.Colors.map(x => x.Color));          

          if (!colors.contains(record.color) && record.color.replace('/(^[0-9A-Za-z]+)/', '') == '') {
            let candidates = colors.filter(i => i.indexOf(record.color) > -1);
            if (candidates.length == 1) record.color = candidates[0];
          }

          if (!colors.contains(record.color)) {

            if (!adjustments[key]) {

              let test = products.first(i => i.UnitPrice == record.price);

              adjustments[key] = test ? { ...test } : { ID: 0, SKU: record.sku, Colors: [], Unit: ProductUnit.Meter, AverageQuantity: 0, UnitPrice: record.price, IsPriceVerified: false, LogisticsMargin: this.configs.defaultLogisticsMargin, ProfitMargin: this.configs.defaultProfitMargin };
            }

            adjustments[key].Colors.push({ Available: 0, Color: record.color });
            news.push({ sku: record.sku, color: record.color, profit: adjustments[key].ProfitMargin, logistics: adjustments[key].LogisticsMargin, price: record.price });
          }
        });

        if (news.length > 0) {

          let modal = this.modal.show(ConfirmerComponent, { state: { data: news } }).content;

          modal.onConfirm.subscribe(() => {

            for (let o in adjustments) {
              let product = adjustments[o];
              this.sync.with(ImportsComponent).repeat(() => this.ds.products(product.ID > 0 ? 'update' : 'create', product).pipe(map(data => data && data.length > 0)));              
            }

            this.sync.when(ImportsComponent).commit(() => {

              this.regenerateLookups(() => {

                this.regenerateDisplayingGroups(() => {

                  records.forEach(i => {

                    let key = [i.order, i.sku, i.color, i.price].join('/');
                    let detail = lu.products.get(key);

                    if (!detail || detail.providable < 1) {
                      key = [0, i.sku, i.color, 0].join('/');
                      detail = lu.products.get(key);
                    }

                    this.add(detail, i.actualLength, i.quoteLength, i.price);

                  });
                });
              });
            });
          });

          modal.onCancel.subscribe(() => {
            this.importData(text);
          });
        }
        else {

          this.sync.when(ImportsComponent).commit(() => {

            this.regenerateDisplayingGroups(() => {

              records.forEach(i => {

                let key = [i.order, i.sku, i.color, i.price].join('/');
                let detail = lu.products.get(key);

                if (!detail || detail.providable < 1) {
                  key = [0, i.sku, i.color, 0].join('/');
                  detail = lu.products.get(key);
                }

                this.add(detail, i.actualLength, i.quoteLength, i.price);

              });
            });
          });
        }
      });
    });
  }

  constructor(
    private layout: LayoutService,
    private ds: DataService,
    private api: ApiService,
    private modal: ModalService,
    private sync: SynchronizationService,
    private partners: PartnersService,
    private products: ProductsService,
    private configs: ConfigsService
  ) {
  }

  public get loading(): boolean {
    return !this.sync.ready(PartnersService, ProductsService, ImportsComponent);
  }

  public invoices = new Filterable<InvoiceRecord>({
    extractor: i => [i.Tag]    
  });

  public orders = new Filterable<OrderRecord>({ extractor: i => [i.Tag, this.partners.get(i.PartnerID).Name] });
  public details = new Filterable<OrderDetail>({ extractor: i => [i.SKU + 'm' + i.Color] });

  public selected: { invoice: InvoiceRecord, order: OrderRecord, groups: Group[][] } = { invoice: null, order: null, groups: [] };
  private edited: boolean = false;

  public editStamp() {
    let ref = this.modal.show(CalendarComponent, { state: { selected: new Date(this.selected.invoice.Stamp) } });

    ref.content.change.subscribe(value => {
      this.selected.invoice.Stamp = value.getTime();
      ref.hide();
      this.edited = true;
    });
  }

  public editTag() {

    let ref = this.modal.show(TagEditorComponent, { state: { tag: { InvoiceID: this.selected.invoice.ID, Tag: this.selected.invoice.Tag, PartnerID: this.selected.invoice.PartnerID, Stamp: this.selected.invoice.Stamp } } });

    ref.content.change.subscribe(value => {
      this.selected.invoice.Tag = value.Tag;
      ref.hide();
    });
  }


  ngOnInit(): void {

    main(() => {
      this.layout.media('footer', 'none');
      this.layout.title = Multilingual.getText("Lên đơn hàng về từ xưởng");
    });

    this.synchronize();
  }

  public get hasGenericOrder(): boolean {
    return this.selected.groups.sum(i => i.count(x => x.OrderID == 0)) > 0;
  }

  public warehouseCount(group: Group): number {
    return group.records.length - group.Partners.sum(i => i.Provided);
  }

  public get editable(): boolean {
    return this.selected.invoice && this.selected.invoice.State == InvoiceState.Editable;
  }

  public get creatable(): boolean {
    return !this.selected.invoice && this.selected.order && this.selected.order.Details.count(i => i.Quantity > i.Provided) > 0;
  }

  public get savable(): boolean {
    return this.selected.invoice != null && this.selected.invoice.Details.length > 0 && this.selected.invoice.Details.count(i => i.ActualQuantity <= 0 || i.QuoteUnitPrice <= 0) < 1 && this.edited;
  }

  public get confirmable(): boolean {
    return this.selected.invoice != null && this.selected.invoice.State == InvoiceState.Editable && this.selected.invoice.Details.length > 0 && this.selected.invoice.Details.count(i => i.QuoteQuantity <= 0 || i.QuoteUnitPrice <= 0) < 1 && !this.edited;
  }

  public get cancelable(): boolean {
    return this.selected.invoice != null && this.selected.invoice.State == InvoiceState.Editable && ((this.selected.invoice.Details.length > 0 && !this.edited) || this.selected.invoice.ID == 0 || (this.selected.invoice.Details.length < 1 && this.edited));
  }

  public partner(order: OrderRecord): PartnerRecord {
    let partner = this.partners.get(order.PartnerID);
    return partner ? partner : { ID: order.PartnerID, Name: 'TỰ CHIA', Address: null, Phone: null, Email: null, DealerID: 0, ImportPolicyID: 0, ExportPolicyID: 0 };
  }

  public statistics(field: string, groups: Group[], unit?: ProductUnit): number {
    switch (field) {
      case "SKU": return groups.filter(i => !unit || unit == i.Unit).group(i => i.SKU).length;
      case "Color": return groups.filter(i => !unit || unit == i.Unit).group(i => i.SKU + "/" + i.Color).length;
      case "Count": return groups.filter(i => !unit || unit == i.Unit).sum(i => i.records.length);
      case "ActualQuantity": return groups.filter(i => !unit || unit == i.Unit).sum(i => i.records.sum(x => x.ActualQuantity));
      case "ReducedQuantity": return groups.filter(i => !unit || unit == i.Unit).sum(i => i.Reduced);
      case "QuoteQuantity": return groups.filter(i => !unit || unit == i.Unit).sum(i => i.records.sum(x => x.ActualQuantity) - i.Reduced);
      case "Total": return groups.filter(i => !unit || unit == i.Unit).sum(i => (i.records.sum(x => x.ActualQuantity) - i.Reduced) * i.QuotePrice);
      default: return 0;
    }

  }

  public state = invoiceState;

  public countProducts(invoice: InvoiceRecord) {
    return invoice.Details.group(i => i.SKU + '/' + i.Color).length;
  }

  public quoteQuantity(invoice: InvoiceRecord) {
    return invoice.Details.sum(i => i.QuoteQuantity);
  }

  public sum(field: string, unit?: ProductUnit): number {
    return this.statistics(field, this.selected.groups.many(i => i), unit);
  }

  public get empty(): boolean {
    return this.selected.groups.count(i => i.length > 1) < 1 && this.selected.groups.count(i => i.length > 0) < 2;
  }

  public extractor(field: string): SortableExtractor<InvoiceRecord> {
    return {
      extract: i => i[field]
    }
  }

  private regenerateAddableObjects() {
    if (this.selected.order) {
      this.details.source = this.selected.order.Details.filter(i => i.Provided < i.Quantity || i.ImportUnitPrice == 0).map(i => i as OrderDetail);
    }
    else {
      this.details.source = [];      
    }

    this.orders.source = lu.orders.source.filter(i => i.Details.count(i => i.Provided < i.Quantity) > 0);
  }

  public toggle(order: OrderRecord) {
    this.selected.order = order == this.selected.order ? null : order;
    this.regenerateAddableObjects();
  }

  private synchronize() {

    this.sync.with(ImportsComponent).repeat(() => this.ds.invoices('imports').pipe(map(data => data ? commit<boolean>(() => this.invoices.source = data.filter(i => i.PartnerID == 1 && i.State == InvoiceState.Editable)).return(true) : false)));
  }

  private regenerateLookups(then?: () => void) {

    OrderDetail.clear();

    this.products.synchronize();

    let orders: OrderRecord[] = [];

    this.sync.with(ImportsComponent).repeat(() => this.ds.orders('incompleted').pipe(map(data => data ? commit<boolean>(() => orders = data).return(true) : false)));

    this.sync.when(ImportsComponent, ProductsService).commit(() => {

      orders = orders.filter(i => !i.Completed && i.State == OrderState.Confirmed && i.Notified);
      orders.commit(i => i.Details = i.Details.map(x => new OrderDetail(x)));

      this.products.all.commit(i => i.Colors.commit(c => new OrderDetail({
        ID: 0,
        OrderID: 0,
        SKU: i.SKU,
        Color: c.Color,
        Unit: i.Unit,
        ExpectedDelay: 0,
        ExpectedUnitQuantity: 0,
        ImportUnitPrice: 0,
        Provided: 0,
        Quantity: 0,
        QuoteConversion: 0,
        QuoteUnitPrice: 0,
        Verified: false
      })));

      lu.orders.source = orders.union([{
        ID: 0,
        Tag: Multilingual.getText("TỰ CHIA"),
        PartnerID: 3,
        ImportCurrency: 'CNY',
        QuoteCurrency: 'VND',
        State: OrderState.Confirmed,
        FirstStamp: new Date().getTime(),
        LastStamp: new Date().getTime(),
        CreatorID: 0,
        UpdatorID: 0,
        Stamp: new Date().getTime(),
        Completed: false,
        Notified: true,
        Details: OrderDetail.genericRecords.map(i => i as OrderDetailRecord)
      }]);

      lu.products.source = lu.orders.source.many(i => i.Details as OrderDetail[]);

      if (then) then();
    });
  }

  private regenerateDisplayingGroups(then?: () => void) {

    this.sync.when(ImportsComponent, ProductsService, PartnersService).commit(() => {

      this.selected.groups = this.selected.invoice.Details.group(i => i.ImportOrderID).map(i => i.items.group(x => [i.key, x.SKU, x.Color, x.QuoteUnitPrice, x.IsPriceEditable].join('/')).map(x => {

        let product: OrderDetail = lu.products.get([i.key, x.items[0].SKU, x.items[0].Color, x.items[0].IsPriceEditable ? 0 : x.items[0].QuoteUnitPrice].join('/'));

        return {
          OrderID: lu.orders.get(i.key).ID,
          OrderTag: lu.orders.get(i.key).Tag,
          Partners: product ? product.getPartners(this.partners, this.selected.invoice) : [],
          SKU: x.items[0].SKU,
          Color: x.items[0].Color,
          Unit: x.items[0].Unit,
          QuotePrice: x.items[0].QuoteUnitPrice,
          Reduced: x.items.sum(o => o.ActualQuantity - o.QuoteQuantity),
          IsPriceEditable: x.items[0].IsPriceEditable,
          records: x.items
        };
      }));

      if (then) then();
    });
  }

  public open(invoice: InvoiceRecord, then?: () => void) {

    if (invoice.ID > 0 && invoice.Details.length == 0) {

      this.sync.with(ImportsComponent).repeat(() => this.ds.invoices('get', { ID: invoice.ID }).pipe(map(invoices => {
        if (invoices && invoices.length == 1) {
          invoice.Details = invoices[0].Details;

          return true;
        }
        return false;
      })));
    }

    if (invoice.State == InvoiceState.Confirmed) {

      this.sync.when(PartnersService, ImportsComponent).commit(() => {
        let modal = this.modal.show(ViewerComponent, { state: { invoice: invoice } });
        modal.content.unconfirmed.subscribe(result => invoice.State == result.State);
      });
      return;
    }

    this.regenerateLookups(() => {

      this.orders.source = lu.orders.source.filter(i => i.Details.count(i => i.Provided < i.Quantity) > 0);

      if (invoice.ID > 0) {
        invoice.Details.filter(i => i.ImportOrderID > 0).group(i => [i.ImportOrderID, i.SKU, i.Color, i.QuoteUnitPrice].join('/')).forEach(x => lu.products.get(x.key).setProvided(x.items.length, invoice, undefined, 'initialize'));
        OrderDetail.setAllocations(invoice, invoice.Allocations);
      }

      this.selected.invoice = { ...invoice, Details: invoice.Details.map(i => ({ ...i })) };

      this.regenerateDisplayingGroups(then);
    });
  }

  public close() {

    if (this.edited) {

      this.modal.show(MessageBoxComponent, {
        ignoreBackdropClick: true, state: {
          closable: false,
          message: Multilingual.getText("Đơn nhập hàng có các thay đổi chưa lưu lại. Đóng đơn sẽ không lưu lại các thay đổi đó. Bạn có muốn đóng đơn?"),
          title: Multilingual.getText("Xác nhận"),
          buttons: [
            {
              icon: 'fa fa-check', class: 'bg-warning text-white', name: Multilingual.getText('Đóng đơn'), click: e => {

                this.selected.order = null;
                this.selected.invoice = null;
                this.selected.groups = [];
                this.edited = false;
              }
            },

            { icon: "fa fa-undo", class: 'bg-success text-white', name: 'Quay lại' }
          ]
        }
      });
    }
    else {

      this.selected.order = null;
      this.selected.invoice = null;
      this.selected.groups = [];
    }
  }

  public create() {

    this.open({
      ID: 0,
      Tag: null,
      CreatorID: this.api.user.ID,
      UpdatorID: this.api.user.ID,
      Icon: '¥',
      Symbol: 'CNY',
      State: InvoiceState.Editable,
      Type: InvoiceType.Import,
      Category: InvoiceCategory.Qualified,
      FirstStamp: new Date().getTime(),
      LastStamp: new Date().getTime(),
      Stamp: new Date().getTime(),
      Details: [],
      Allocations: [],
      PartnerID: 1,
      Total: 0
    }, () => this.editStamp());
  }

  public add(product: OrderDetail, actualLength: number = 0, quoteLength: number = 0, price: number = NaN) {

    price = !isNaN(price) ? price : product.ImportUnitPrice;

    let detail: Group = this.selected.groups.many(i => i).first(i => i.OrderID == product.OrderID && i.SKU == product.SKU && i.Color == product.Color && i.QuotePrice == price, {
      OrderID: product.OrderID,
      OrderTag: lu.orders.get(product.OrderID).Tag,
      Partners: product.getPartners(this.partners, this.selected.invoice),
      SKU: product.SKU,
      Color: product.Color,
      Unit: product.Unit,
      QuotePrice: price,
      IsPriceEditable: product.ImportUnitPrice == 0,
      Reduced: 0,
      records: []
    });
   
    let order: Group[] = this.selected.groups.first(i => i[0].OrderID == detail.OrderID, []);

    let max = product.getProvidable(this.selected.invoice)

    let record: InvoiceDetailRecord = {
      ID: 0,
      SKU: product.SKU,
      Color: product.Color,
      Unit: product.Unit,
      ActualQuantity: actualLength,
      QuoteQuantity: quoteLength,
      QuoteUnitPrice: price,
      IsPriceEditable: product.ImportUnitPrice == 0,
      ImportOrderID: max < 1 ? 0 : product.OrderID,
      ExportOrderID: max < 1 ? 0 : product.OrderID,
    };

    this.selected.invoice.Details.push(record);
    detail.records.push(record);
    detail.Reduced += actualLength - quoteLength;

    if (!order.contains(detail)) order.push(detail);
    if (!this.selected.groups.contains(order)) this.selected.groups.push(order);

    product.addProvided(1, this.selected.invoice);

    this.regenerateAddableObjects();

    this.edited = true;

    if (this.details.empty) this.toggle(this.selected.order);    
  }

  public remove(records: InvoiceDetailRecord[]): number {

    let counts: { [key: string]: number } = {};    

    for (let record of records.map(i => i)) {
      if (!this.selected.invoice.Details.remove(record)) continue;

      let detail: Group = this.selected.groups.many(i => i).first(i => i.records.contains(record));
      let order: Group[] = this.selected.groups.first(i => i.contains(detail));

      detail.records.remove(record);
      detail.Reduced -= record.ActualQuantity - record.QuoteQuantity;

      if (detail.records.length < 1) {
        order.remove(detail);
        if (order.length < 1) {
          this.selected.groups.remove(order);
        }
      }

      if (this.warehouseCount(detail) < 0 || detail.records.length < 1) {        

        let key = [record.ImportOrderID, record.SKU, record.Color, record.QuoteUnitPrice].join('/');
        if (!counts[key]) counts[key] = 0;
        counts[key]++;
      }
    }

    let count = 0;

    for (let key in counts) {
      count += counts[key];
      lu.products.get(key).addProvided(-counts[key], this.selected.invoice);
    }

    if (count > 0) {

      this.regenerateAddableObjects();

      this.edited = true;
    }

    return count;
  }

  public setPrice(detail: Group, input: NumberInputComponent) {
    if (isNaN(input.value) || input.value < 1) {
      input.value = 0;
    }

    detail.QuotePrice = input.value;
    detail.records.commit(i => i.QuoteUnitPrice = input.value);
  }

  public setCount(detail: Group, input: NumberInputComponent) {

    if (isNaN(input.value) || input.value < 1) {
      input.value = 0;
      this.remove(detail.records);
    }

    let product = lu.products.get([detail.OrderID, detail.SKU, detail.Color, detail.OrderID > 0 ? detail.QuotePrice : 0].join('/'));

    if (product && !product.isGeneric) input.value = Math.min(product.getProvidable(this.selected.invoice) + detail.records.length, input.value);

    let count = input.value - detail.records.length;
    let reduced = detail.records.length > 0 ? detail.records.sum(i => i.ActualQuantity - i.QuoteQuantity) / detail.records.length : 0;

    this.remove(detail.records.skip(input.value));    

    for (let i = 0; i < count; i++) this.add(product, 0, -reduced, detail.QuotePrice);
  }

  public setGenericCount(partner: Partner, input: NumberInputComponent) {
    if (isNaN(input.value) || input.value < 1) {
      input.value = 0;
    }
    
    partner.Provided = input.value;

    if (partner.Provided != input.value) input.value = partner.Provided;
    this.edited = true;
  }

  public setReduce(product: { SKU: string, Color: string, Reduced: number, records: InvoiceDetailRecord[] }, input: NumberInputComponent) {

    if (isNaN(input.value) || input.value < 1) {
      input.value = 0;
    }

    product.Reduced = input.value;
    this.edited = true;
  }  

  public setLength(detail: InvoiceDetailRecord, input: NumberInputComponent) {    

    if (isNaN(input.value)) {
      this.remove([detail]);
    }
    else {

      detail.QuoteQuantity = input.value - (detail.ActualQuantity - detail.QuoteQuantity);
      detail.ActualQuantity = input.value;

    }

    this.edited = true;
  }

  public save() {

    this.selected.groups.many(i => i).forEach(i => {

      let reducedPerItem = i.Reduced / i.records.length;

      i.records.forEach(r => {
        r.QuoteQuantity = r.ActualQuantity - reducedPerItem;
      });
    });

    this.selected.invoice.Allocations = OrderDetail.getAllocations(this.selected.invoice);

    this.sync.with(ImportsComponent).commit(done => {
      this.ds.invoices(this.selected.invoice.ID > 0 ? 'update' : 'create', this.selected.invoice).subscribe(invoices => {

        if (invoices && invoices.length == 1) {

          this.selected.invoice = invoices[0];
          this.edited = false;
          this.synchronize();
        }

        done();
      });
    });    
  }

  public cancel() {

    if (this.selected.invoice.ID == 0 && this.selected.invoice.Details.length == 0) {
      this.edited = false;
      this.close();
      return;
    }

    this.modal.show(MessageBoxComponent, {
      ignoreBackdropClick: true, state: {
        closable: false,
        message: "Bạn muốn hủy đơn nhập hàng đang mở?",
        title: "Xác nhận",
        buttons: [
          {
            icon: 'fa fa-times', class: 'bg-danger text-white', name: 'Hủy đơn', click: e => {

              if (this.selected.invoice.ID == 0) {                                
                this.edited = false;
                this.close();
              }
              else {

                this.sync.with(ImportsComponent).commit(done => {
                  this.ds.invoices('cancel', this.selected.invoice).subscribe(invoice => {
                    if (invoice) {
                      this.edited = false;
                      this.close();
                      this.synchronize();
                    }
                    done();
                  });
                });
              }
            }
          },

          { icon: "fa fa-undo", class: 'bg-success text-white', name: 'Quay lại' }
        ]
      }
    });
  }
}

export class Partner implements PartnerRecord {

  private source: PartnerRecord;
  private invoice: InvoiceRecord;
  private detail: OrderDetail;

  public get ID(): number {
    return this.source.ID;
  }

  public get Name(): string {
    return this.source.Name;
  }

  public get Address(): string {
    return this.source.Address;
  }

  public get Phone(): string {
    return this.source.Phone;
  }

  public get Email(): string {
    return this.source.Email;
  }

  public get DealerID(): number {
    return this.source.DealerID;
  }

  public get ImportPolicyID(): number {
    return this.source.ImportPolicyID;
  }

  public get ExportPolicyID(): number {
    return this.source.ExportPolicyID;
  }

  public get Providable(): number {
    return this.detail.getProvidable(this.invoice, this);
  }

  public get Provided(): number {
    return this.detail.getProvided(this.invoice, this);
  }

  public set Provided(value: number) {
    this.detail.setProvided(value, this.invoice, this);
  }

  public get Quantity(): number {
    return this.detail.getQuantity(this.invoice, this);
  }

  constructor(partner: PartnerRecord, detail: OrderDetail, invoice: InvoiceRecord) {
    this.source = partner;
    this.detail = detail;
    this.invoice = invoice;
  }
}

export class OrderDetail implements OrderDetailRecord {

  private static _genericRecordLU: { [key: string]: OrderDetail } = {};
  private static _genericRecords: OrderDetail[] = [];

  private source: OrderDetailRecord;
  private generatable: boolean = false;
  private allocated: { [key: number]: number } = {} // key is an invoice id

  private links: OrderDetail[] = [];

  private get totalAllocated(): number {
    let result = 0;
    for (let i in this.allocated) result += this.allocated[i];
    return result;
  }

  public static get genericRecords(): OrderDetail[] {

    return this._genericRecords.filter(i => i.links.sum(i => i.source.Quantity - i.source.Provided) > 1 || i.source.ImportUnitPrice == 0);
  }

  public get ID(): number {
    return this.source.ID;
  }

  public get OrderID(): number {
    return this.source.OrderID;
  }

  public get SKU(): string {
    return this.source.SKU;
  }

  public get Color(): string {
    return this.source.Color;
  }

  public get Unit(): ProductUnit {
    return this.source.Unit;
  }

  public get ImportUnitPrice(): number {
    return this.source.ImportUnitPrice;
  }

  public get QuoteUnitPrice(): number {
    return this.source.QuoteUnitPrice;
  };

  public get QuoteConversion(): number {
    return this.source.QuoteConversion;
  }

  public get Verified(): boolean {
    return this.source.Verified;
  }

  public get ExpectedDelay(): number {
    return this.source.ExpectedDelay;
  }

  public get ExpectedUnitQuantity(): number {
    return this.source.ExpectedUnitQuantity;
  }

  public get Provided(): number {
    return this == this.generic ? this.links.sum(i => i.totalAllocated) : this.source.Provided;
  }

  public get Quantity(): number {
    return this == this.generic ? this.links.sum(i => i.source.Quantity - i.source.Provided) : this.source.Quantity - this.totalAllocated;
  }

  public get providable(): number {
    return this.Quantity - this.Provided;
  }

  public get isGeneric(): boolean {
    return this == this.generic;
  }

  public get groupKey(): string {
    return this.SKU + "/" + this.Color + "/" + this.ImportUnitPrice;
  }

  public get generic(): OrderDetail {
    return OrderDetail._genericRecordLU[this.groupKey];
  }

  public static clear() {
    this._genericRecordLU = {};
    this._genericRecords = [];
  }

  public static getAllocations(invoice: InvoiceRecord): InvoiceAllocationRecord[] {
    return this._genericRecords.many(i => i.links).filter(i => i.getProvided(invoice, undefined, 'generic') > 0).map(i => ({ OrderID: i.OrderID, SKU: i.SKU, Color: i.Color, QuoteUnitPrice: i.ImportUnitPrice, Quantity: i.getProvided(invoice, undefined, 'generic') }));
  }

  public static setAllocations(invoice: InvoiceRecord, value: InvoiceAllocationRecord[]) {

    let lu = new Lookup<InvoiceAllocationRecord>(i => [i.OrderID, i.SKU, i.Color, i.OrderID > 0 ? i.QuoteUnitPrice : 0].join('/'));
    lu.source = value.commit(i => i.CustomerID = i.OrderID > 0 ? i.CustomerID : 3);

    this._genericRecords.many(i => i.links).forEach(i => {
      let key = [i.OrderID, i.SKU, i.Color, i.ImportUnitPrice].join('/');
      i.setProvided(lu.has(key) ? lu.get(key).Quantity : 0, invoice, undefined, 'generic');
    });
  }

  private getAllocated(invoice: InvoiceRecord, mode: 'generic' | 'specific' | 'both'): number {

    switch (mode) {
      case 'generic':

        if (!this.allocated[-1 - invoice.ID]) this.allocated[-1 - invoice.ID] = 0;
        return this.allocated[-1 - invoice.ID];

      case 'specific':

        if (!this.allocated[invoice.ID]) this.allocated[invoice.ID] = 0;
        return this.allocated[invoice.ID];

      case 'both':

        return this.getAllocated(invoice, 'generic') + this.getAllocated(invoice, 'specific');

      default: return 0;
    }
  }

  private setAllocated(value: number, invoice: InvoiceRecord, mode: 'generic' | 'specific') {

    let id = mode == 'generic' ? -1 - invoice.ID : invoice.ID;
    this.allocated[id] = value;
  }

  public getPartners(provider: PartnersService, invoice: InvoiceRecord): Partner[] {

    if (this == this.generic) {
      return this.links.map(i => lu.orders.get(i.OrderID).PartnerID).distinct().map(i => new Partner(provider.get(i), this, invoice));
    }
    else {
      return [new Partner(provider.get(lu.orders.get(this.OrderID).PartnerID), this, invoice)];
    }
  }

  public isPartner(partner: PartnerRecord): boolean {
    return lu.orders.get(this.OrderID).PartnerID == partner.ID;
  }

  public getQuantity(invoice: InvoiceRecord, partner?: PartnerRecord, mode?: 'generic' | 'specific'): number {

    if (this.isGeneric) {

      if (typeof mode == 'undefined') mode = 'generic';
      return this.links.sum(i => i.getQuantity(invoice, partner, mode));

    } else {

      if (typeof mode == 'undefined') mode = 'specific';

      if (typeof partner == 'undefined' || this.isPartner(partner)) {
        return this.source.Quantity - this.source.Provided - this.totalAllocated + this.getAllocated(invoice, mode);
      }

      return 0;
    }
  }

  public getProvided(invoice: InvoiceRecord, partner?: PartnerRecord, mode?: 'generic' | 'specific'): number {

    if (this.isGeneric) {

      if (typeof mode == 'undefined') mode = 'generic';
      return this.links.sum(i => i.getProvided(invoice, partner, mode));

    } else {

      if (typeof mode == 'undefined') mode = 'specific';

      return typeof partner == 'undefined' || this.isPartner(partner)
        ? this.getAllocated(invoice, mode)
        : 0;
    }
  }

  public getProvidable(invoice: InvoiceRecord, partner?: PartnerRecord, mode?: 'generic' | 'specific'): number {
    return this.getQuantity(invoice, partner, mode) - this.getProvided(invoice, partner, mode);
  }

  public setProvided(value: number, invoice: InvoiceRecord, partner?: PartnerRecord, mode?: 'generic' | 'specific' | 'initialize') {

    if (this.isGeneric) {

      if (typeof partner != 'undefined') {

        let keep = this.getProvided(invoice);

        value = Math.max(0, Math.min(this.getQuantity(invoice, partner), value));

        let refs = this.links.filter(i => i.isPartner(partner)).commit(i => i.generatable = false);

        let current = this.getProvided(invoice, partner);
        if (value == current) return;

        let generatable = this.links.filter(i => i.generatable && (value > current ? i.getProvided(invoice, undefined, 'generic') > 0 : i.getProvidable(invoice, undefined, 'generic') > 0));
        if (generatable.length < 1) generatable = this.links.filter(i => !refs.contains(i)).commit(i => i.generatable = true);


        let max = generatable.sum(i => i.getProvided(invoice, undefined, 'generic'));
        let min = -generatable.sum(i => i.getProvidable(invoice, undefined, 'generic'));

        let chg = Math.max(min, Math.min(max, value - current));

        value = keep;

        for (let i of refs) {

          if (chg > i.getProvidable(invoice, partner, 'generic')) {

            chg -= i.getProvidable(invoice, partner, 'generic');
            i.setProvided(i.getQuantity(invoice, partner, 'generic'), invoice, partner, 'generic');
          }
          else if (chg < - i.getProvided(invoice, partner, 'generic')) {
            chg += i.getProvided(invoice, partner, 'generic');
            i.setProvided(0, invoice, partner, 'generic');
          }
          else {
            i.addProvided(chg, invoice, partner, 'generic');
            break;
          }
        }
      }

      {
        let change = Math.max(-this.getProvided(invoice), Math.min(this.getProvidable(invoice), value - this.getProvided(invoice)));

        let refs = this.links.commit(i => i.generatable = i.generatable || typeof partner == 'undefined').filter(i => i.generatable);

        while (change != 0 && refs.length > 0) {

          refs = refs.order(i => i.getProvided(invoice, undefined, 'generic'), change > 0 ? 'ascending' : 'descending');

          let i = refs.first();
          let test = i.getProvided(invoice, undefined, 'generic') + (change > 0 ? 1 : -1);

          if (test < 0 || test > i.getQuantity(invoice, undefined, 'generic')) {
            refs = refs.skip(1);
            continue;
          }

          i.setProvided(test, invoice, undefined, 'generic');
          change += (change > 0 ? -1 : 1);
        }
      }
    }
    else {
      if (typeof partner == 'undefined' || this.isPartner(partner)) {

        if (typeof mode == 'undefined') mode = 'specific';

        switch (mode) {

          case 'initialize':

            this.source.Provided -= value;
            this.setAllocated(value, invoice, 'specific');
            break;

          default:

            this.setAllocated(value, invoice, mode as ('generic' | 'specific'));
            break;
        }        
      }
    }
  }

  public addProvided(value: number, invoice: InvoiceRecord, partner?: PartnerRecord, mode?: 'generic' | 'specific') {
    this.setProvided(this.getProvided(invoice, partner, mode) + value, invoice, partner, mode)
  }

  constructor(src: OrderDetailRecord) {

    this.source = src;

    if (!isNaN(src.Quantity)) {

      if (!OrderDetail._genericRecordLU[this.groupKey]) {
        let generic: OrderDetail = new OrderDetail({ ...src, OrderID: 0, ID: 0, Quantity: NaN, Provided: 0, QuoteUnitPrice: NaN, QuoteConversion: NaN, Verified: false });
        OrderDetail._genericRecordLU[this.groupKey] = generic;
        OrderDetail._genericRecords.push(generic);
      }

      OrderDetail._genericRecordLU[this.groupKey].links.push(this);
    }
  }
}
